import React, { useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ExportProductTier,
  UpdateExportProductInput,
} from 'api/ExportHubAPI';
import { Field, EditableField } from './productDetailShell';
import StyledSingleSelect from './StyledSingleSelect';
import CountryMultiSelect from './CountryMultiSelect';
import { TIER_OPTIONS, TierBadge } from './productPresets';
import {
  applyLicenseOverride,
  licenseOverrideToColumn,
  LicenseOverride,
  LicenseRequirement,
  readLicenseOverride,
} from './licenseRequirement';

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  dbName: string;
  onChanged: () => void;
  // Derived from Country Screening — shown in place of the raw ECCN, which is
  // the input rather than the answer people are looking for. A manual answer on
  // the product overrides it; this component owns that edit, so it applies it.
  screeningRequirement: LicenseRequirement;
  // Called after save when the ECCN changed — ECCN drives the CCL/Country/ITAR/
  // NRC checks, so the parent re-runs classification to regenerate them.
  onReclassify?: () => void;
}

const EditableProductDetails: React.FC<Props> = ({
  product,
  groupId,
  dbName,
  onChanged,
  screeningRequirement,
  onReclassify,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState({
    description: product.description ?? '',
    manufacturer: product.manufacturer ?? '',
    country_of_origin: product.country_of_origin ?? '',
    internal_sku: product.internal_sku ?? '',
    hts_code: product.hts_code ?? '',
    eccn: product.eccn ?? '',
    materials: product.materials ?? '',
    intended_end_use: product.intended_end_use ?? '',
    quantity: product.quantity !== null ? String(product.quantity) : '',
    unit_of_measure: product.unit_of_measure ?? '',
  });
  const [hw, setHw] = useState(product.hardware_authorized);
  const [td, setTd] = useState(product.tech_data_authorized);
  const [tier, setTier] = useState<ExportProductTier>(product.tier);
  const [destinations, setDestinations] = useState<string[]>(
    product.intended_destination_countries || [],
  );
  // '' = take the screening answer; 'Yes'/'No' = the reviewer has answered it.
  const [licReq, setLicReq] = useState<'' | LicenseOverride>(
    readLicenseOverride(product.license_required_override) ?? '',
  );
  const set = (k: keyof typeof v, val: string) =>
    setV((p) => ({ ...p, [k]: val }));

  const startEdit = () => {
    setV({
      description: product.description ?? '',
      manufacturer: product.manufacturer ?? '',
      country_of_origin: product.country_of_origin ?? '',
      internal_sku: product.internal_sku ?? '',
      hts_code: product.hts_code ?? '',
      eccn: product.eccn ?? '',
      materials: product.materials ?? '',
      intended_end_use: product.intended_end_use ?? '',
      quantity: product.quantity !== null ? String(product.quantity) : '',
      unit_of_measure: product.unit_of_measure ?? '',
    });
    setHw(product.hardware_authorized);
    setTd(product.tech_data_authorized);
    setTier(product.tier);
    setDestinations(product.intended_destination_countries || []);
    setLicReq(readLicenseOverride(product.license_required_override) ?? '');
    setEditing(true);
  };

  const save = async () => {
    if (!groupId) return;
    setSaving(true);
    const qty = v.quantity.trim();
    const eccnChanged = v.eccn.trim() !== (product.eccn ?? '').trim();
    const input: UpdateExportProductInput = {
      // Sent even when cleared so a mistyped SKU can actually be removed.
      internal_sku: v.internal_sku.trim(),
      tier: tier,
      description: v.description.trim() || undefined,
      manufacturer: v.manufacturer.trim() || undefined,
      country_of_origin: v.country_of_origin.trim() || undefined,
      hts_code: v.hts_code.trim() || undefined,
      eccn: v.eccn.trim() || undefined,
      materials: v.materials.trim() || undefined,
      intended_end_use: v.intended_end_use.trim() || undefined,
      quantity: qty === '' ? undefined : Number(qty),
      unit_of_measure: v.unit_of_measure.trim() || undefined,
      hardware_authorized: hw,
      tech_data_authorized: td,
      intended_destination_countries: destinations,
      // Its own scalar column, so this is a plain assignment — no read of the
      // current row, and nothing else on the product to clobber. Sent every
      // save; null means "no manual answer, derive it".
      license_required_override: licenseOverrideToColumn(licReq),
    };
    try {
      await ExportHubAPI.updateProduct(product.id, groupId, input);
      enqueueSnackbar('Product details saved.', { variant: 'success' });
      setEditing(false);
      onChanged();
      // ECCN drives most of the six checks — regenerate them when it changes.
      if (eccnChanged && v.eccn.trim() && onReclassify) onReclassify();
    } catch (err) {
      console.error('Failed to save product details:', err);
      enqueueSnackbar('Failed to save product details.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // The screening answer with the product's saved manual answer applied. The
  // read view is the only consumer, so the live `licReq` edit state is
  // deliberately not used here — it is not the truth until it is saved.
  const licenseRequirement = applyLicenseOverride(
    screeningRequirement,
    readLicenseOverride(product.license_required_override),
  );
  // Without an ECCN the derived note says the same thing as the warning under
  // it, so only one of the two is shown. A manual answer's note says something
  // different (what screening disagrees about), so that one always shows.
  const showLicReqDetail =
    !!product.eccn || licenseRequirement.source === 'manual';

  const htsValue = editing ? (
    <EditableField
      editing
      label='HTS / Schedule B'
      value={v.hts_code}
      mono
      onChange={(x) => set('hts_code', x)}
    />
  ) : product.hts_code ? (
    <Field label='HTS / Schedule B' value={product.hts_code} mono />
  ) : (
    <div className='eh-field-cell'>
      <div className='eh-field-cell-label'>HTS / Schedule B</div>
      <div className='eh-hts-warn eh-sev-warning'>
        <WarningAmberIcon style={{ fontSize: 13 }} /> Not set &mdash; add a code
        to enable the Schedule B and Country checks
      </div>
    </div>
  );

  return (
    <div className='eh-panel'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>Product Details</span>
        <div className='eh-panel-head-actions'>
          {editing ? (
            <>
              <button
                type='button'
                className='btn-outline'
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn-primary'
                onClick={save}
                disabled={saving}
                style={{ opacity: saving ? 0.6 : 1 }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : (
            <button className='eh-mini-btn' type='button' onClick={startEdit}>
              <EditOutlinedIcon style={{ fontSize: 15 }} /> Edit
            </button>
          )}
        </div>
      </div>

      <div className='eh-fieldgrid'>
        {/* Identity — editable so a mistyped SKU or the wrong tier picked
            during upload can be corrected here. */}
        <EditableField
          editing={editing}
          label='Product Number / SKU'
          value={v.internal_sku}
          mono
          onChange={(x) => set('internal_sku', x)}
          placeholder='e.g. FD-3'
        />
        {editing ? (
          <label className='eh-field-cell'>
            <span className='eh-field-cell-label'>Tier</span>
            <StyledSingleSelect
              options={TIER_OPTIONS.map((t) => ({ value: t, label: t }))}
              value={tier}
              onChange={(x) => setTier(x as ExportProductTier)}
              inline
            />
          </label>
        ) : (
          <div className='eh-field-cell'>
            <div className='eh-field-cell-label'>Tier</div>
            <div className='eh-field-cell-value'>
              <TierBadge value={product.tier} />
            </div>
          </div>
        )}
        <EditableField
          editing={editing}
          label='Manufacturer'
          value={v.manufacturer}
          onChange={(x) => set('manufacturer', x)}
        />
        <EditableField
          editing={editing}
          label='Country of Origin'
          value={v.country_of_origin}
          onChange={(x) => set('country_of_origin', x)}
        />
        <div className='eh-fieldgrid-hts'>{htsValue}</div>
        {/* The ECCN is the input; whether a licence is required is the answer.
            Read view leads with the answer and keeps the ECCN as its basis;
            edit mode is where the ECCN itself is set, because it drives the
            EAR/CCL, Country, ITAR and NRC checks. */}
        {editing ? (
          <>
            <EditableField
              editing
              label='ECCN'
              value={v.eccn}
              mono
              onChange={(x) => set('eccn', x)}
              placeholder='e.g. 9A610 (leave blank for EAR99 / Not Subject)'
            />
            {/* Country Screening answers this from the ECCN, but a reviewer who
                has read the licence or the CCL entry can answer it directly —
                and has to be able to, since a product with no ECCN never gets a
                screening answer at all. */}
            <label className='eh-field-cell'>
              <span className='eh-field-cell-label'>License Required</span>
              <StyledSingleSelect
                options={[
                  { value: '', label: 'Auto — from Country Screening' },
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' },
                ]}
                value={licReq}
                onChange={(x) => setLicReq(x as '' | LicenseOverride)}
                inline
              />
              <span className='eh-licreq-hint'>
                {licReq
                  ? `Overrides Country Screening, which says ${screeningRequirement.value}.`
                  : `Country Screening says ${screeningRequirement.value}.`}
              </span>
            </label>
          </>
        ) : (
          <div className='eh-field-cell'>
            <div className='eh-field-cell-label'>License Required</div>
            <div className='eh-licreq'>
              <span
                className={`eh-licreq-value ${licenseRequirement.value.toLowerCase()}`}
              >
                {licenseRequirement.value}
              </span>
              {licenseRequirement.source === 'manual' && (
                <span className='eh-licreq-manual'>Manual</span>
              )}
              {showLicReqDetail && (
                <span className='eh-licreq-detail'>
                  {licenseRequirement.detail}
                </span>
              )}
            </div>
            {product.eccn ? (
              <div className='eh-licreq-basis'>
                ECCN <span className='eh-mono'>{product.eccn}</span>
              </div>
            ) : (
              /* One explanation, not two. Without an ECCN the derived detail
                 says the same thing this warning does, so it is suppressed
                 above and this line carries it alone.

                 Scope matters here: the CCL entry and the Country Chart cell
                 are both looked up by ECCN, so those two checks genuinely need
                 one. ITAR and NRC do not — ITAR jurisdiction comes from the
                 USML (22 CFR 121.1) and an ITAR-controlled item has no ECCN at
                 all, and NRC's list is 10 CFR Part 110. They are unrun here
                 only because this pipeline gets all four from one ECCN-keyed
                 call. */
              <div className='eh-hts-warn eh-sev-warning'>
                <WarningAmberIcon style={{ fontSize: 13 }} /> No ECCN &mdash;
                needed for the EAR/CCL and Country Chart checks. Leave blank
                only if the product is EAR99 or not subject to the EAR.
              </div>
            )}
          </div>
        )}
        {editing ? (
          <label className='eh-field-cell'>
            <span className='eh-field-cell-label'>Quantity</span>
            <div className='eh-qty-row'>
              <input
                className='eh-input eh-mono'
                value={v.quantity}
                onChange={(e) => set('quantity', e.target.value)}
                inputMode='numeric'
                style={{ maxWidth: 110 }}
              />
              <input
                className='eh-input'
                value={v.unit_of_measure}
                onChange={(e) => set('unit_of_measure', e.target.value)}
                placeholder='unit'
              />
            </div>
          </label>
        ) : (
          <Field
            label='Quantity'
            value={
              product.quantity !== null
                ? `${product.quantity} ${product.unit_of_measure || ''}`.trim()
                : ''
            }
          />
        )}
        {editing ? (
          <div className='eh-field-cell'>
            <span className='eh-field-cell-label'>Authorizations</span>
            <div className='eh-checkline'>
              <label>
                <input
                  type='checkbox'
                  checked={hw}
                  onChange={(e) => setHw(e.target.checked)}
                />{' '}
                Hardware
              </label>
              <label>
                <input
                  type='checkbox'
                  checked={td}
                  onChange={(e) => setTd(e.target.checked)}
                />{' '}
                Tech data
              </label>
            </div>
          </div>
        ) : (
          <>
            <Field
              label='Hardware Authorized'
              value={product.hardware_authorized ? 'Yes' : 'No'}
            />
            <Field
              label='Tech Data Authorized'
              value={product.tech_data_authorized ? 'Yes' : 'No'}
            />
          </>
        )}
        <Field label='Database' value={dbName} />
        <div className='eh-fieldgrid-full'>
          <EditableField
            editing={editing}
            label='Materials'
            value={v.materials}
            onChange={(x) => set('materials', x)}
          />
        </div>
        <div className='eh-fieldgrid-full'>
          <EditableField
            editing={editing}
            label='Intended End Use'
            value={v.intended_end_use}
            onChange={(x) => set('intended_end_use', x)}
            multiline
          />
        </div>
        <div className='eh-fieldgrid-full'>
          {editing ? (
            <label className='eh-field-cell'>
              <span className='eh-field-cell-label'>Destinations</span>
              <CountryMultiSelect
                value={destinations}
                onChange={setDestinations}
                placeholder='Search countries…'
                ariaLabel='Intended destination countries'
              />
            </label>
          ) : (
            <Field
              label='Destinations'
              value={
                (product.intended_destination_countries || []).join(', ') || '—'
              }
            />
          )}
        </div>
        <div className='eh-fieldgrid-full'>
          <EditableField
            editing={editing}
            label='Description'
            value={v.description}
            onChange={(x) => set('description', x)}
            multiline
          />
        </div>
      </div>
    </div>
  );
};

export default EditableProductDetails;
